import type { FinishNodeState, RenderNode, SchemaData, SerializedHistory } from '@lemonade-hq/chat-engine';
import { NodeType, truncateSerializedSessionHistory } from '@lemonade-hq/chat-engine';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { devWarn } from '@lemonade-hq/ts-helpers';
import { useCallback, useRef } from 'react';
import { ChatEngineClient } from './chat_engine.client';
import type { UseChatServerPersistenceProps, UseChatServerPersistenceReturn } from './usePersistenceHooksTypes';
import type {
  WizardPersistenceContextSubmissionOptions,
  WizardPersistenceLoadSessionProps,
} from './wizard_persistence.context';

// istanbul ignore next -- @preserve - partially tested
const SERVER_TIMEOUT: Milliseconds = (process.env.ENV ?? 'production') === 'production' ? 16_000 : 40_000;

// MAIN

export const useChatServerPersistence = <TPresentation = undefined>({
  abortController,
  setSession,
  setUiState,
  sessionId,
  currentStepId,
  serverBaseUrl: _serverBaseUrl,
  scriptName: scriptNameProp,
  redirectFn,
  serverTimeoutOverride: serverTimeoutOverrideProp,
}: UseChatServerPersistenceProps<TPresentation>): UseChatServerPersistenceReturn<TPresentation> => {
  const chatClient = useRef<ChatEngineClient<TPresentation>>();
  const sessionTimeoutOverrideRef = useRef<Milliseconds | undefined>(serverTimeoutOverrideProp);

  // SETUP - INIT
  const initSession = useCallback(
    async ({
      initialStepName,
      serverTimeoutOverride,
      accessTokenOverride,
      emailCode,
      scriptName,
      serverBaseUrl: sessionServerBaseUrl,
      ...loadSessionProps
    }: WizardPersistenceLoadSessionProps) => {
      chatClient.current = new ChatEngineClient<TPresentation>({
        serverBaseUrl: sessionServerBaseUrl,
        accessTokenOverride,
        emailCode,
      });
      sessionTimeoutOverrideRef.current = serverTimeoutOverride;

      setUiState(val => {
        return { ...val, serverError: null, isLoadingInitialServerData: true };
      });

      let fetchedSession = await chatClient.current.initSession(
        { ...loadSessionProps, scriptName },
        {
          timeout: sessionTimeoutOverrideRef.current ?? SERVER_TIMEOUT,
        },
      );

      if (fetchedSession.history.length === 1) {
        const firstNode = fetchedSession.history[0] as unknown as FinishNodeState<{ redirectTo?: string }> | RenderNode;

        /* istanbul ignore next -- @preserve - missing test */
        if (firstNode.type === NodeType.Finish && firstNode.payload.redirectTo != null) {
          /* istanbul ignore next -- @preserve - missing test */
          await redirectFn?.(firstNode.payload.redirectTo);
        }
      }

      /* istanbul ignore if -- @preserve - missing test */
      if (initialStepName != null) {
        /* istanbul ignore next -- @preserve - missing test */
        const index = fetchedSession.history.findIndex(step => step.name === initialStepName);
        /* istanbul ignore if -- @preserve - missing test */
        if (index > -1) {
          fetchedSession = truncateSerializedSessionHistory({
            session: fetchedSession,
            answeredNodeIndexInHistory: fetchedSession.history.findIndex(step => step.name === initialStepName),
          });
        }
      }

      setSession(fetchedSession);
      setUiState(val => {
        return { ...val, isLoadingInitialServerData: false };
      });
    },
    [redirectFn, setSession, setUiState],
  );

  // ACTIONS - SUBMIT ANSWERS
  const submitAnswersToServer = useCallback(
    async (answers: SchemaData, options?: WizardPersistenceContextSubmissionOptions) => {
      /* istanbul ignore if -- @preserve - missing test */
      if (chatClient.current == null) {
        /* istanbul ignore next -- @preserve - missing test */
        devWarn('Chat client not initialized');
        /* istanbul ignore next -- @preserve - missing test */
        return { history: [] as SerializedHistory<TPresentation>, success: false };
      }

      if (sessionId == null || currentStepId == null) {
        devWarn('Cannot submit without a session ID or current step ID');
        return { history: [] as SerializedHistory<TPresentation>, success: false };
      }

      abortController.current = new AbortController();

      const fetchedSession = await chatClient.current.submitAnswers(
        {
          answers,
          scriptName: scriptNameProp,
          sessionId,
          stepId: currentStepId,
          resync: options?.resync ?? false,
          skipHooks: options?.skipHooks ?? false,
        },
        {
          timeout: sessionTimeoutOverrideRef.current ?? SERVER_TIMEOUT,
          signal: abortController.current.signal,
        },
      );

      setSession(fetchedSession);
      setUiState(val => {
        return { ...val, isSubmittingToServer: false };
      });
      return { history: fetchedSession.history, success: true };
    },
    [currentStepId, scriptNameProp, sessionId, setSession, setUiState, abortController],
  );

  return {
    initSession,
    submitAnswersToServer,
  };
};
